<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>
  <div class="page-wrapper">
    <div class="content">
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
          <div class="head-icons ms-2">
            <a
              href="javascript:void(0);"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              data-bs-original-title="Collapse"
              id="collapse-header"
              @click="toggleHeader"
            >
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-between my-4 flex-wrap gap-2">
        <div class="payroll-btns">
          <router-link to="/payroll/payroll" class="btn btn-white border me-2">
            Payments
          </router-link>
          <router-link to="/payroll/payroll-overtime" class="btn btn-white border me-2">
            Overtime Definitions
          </router-link>
          <router-link to="/payroll/payroll-overtime-entries" class="btn btn-primary me-2">
            Overtime Entries
          </router-link>
          <router-link to="/payroll/payroll-deduction" class="btn btn-white border">
            Deductions
          </router-link>
        </div>
        <div class="d-flex flex-wrap gap-2">
          <button
            type="button"
            class="btn btn-outline-primary d-flex align-items-center"
            data-bs-toggle="modal"
            data-bs-target="#overtime_entries_csv_import_modal"
            v-can="'Payroll.Create'"
          >
            <i class="ti ti-upload me-2"></i>Import CSV
          </button>
          <a
            href="javascript:void(0);"
            data-bs-toggle="modal"
            data-bs-target="#payroll_overtime_entry_modal"
            class="btn btn-primary d-flex align-items-center"
            @click="onAddNew"
            v-can="'Payroll.Create'"
          >
            <i class="ti ti-circle-plus me-2"></i>Add Overtime Entry
          </a>
        </div>
      </div>

      <div class="alert alert-info mb-3">
        <strong>Period matching:</strong>
        Set the frequency and month (or date range) above to match the payroll run
        you are preparing — then click <strong>Refresh</strong> to view entries.
        For October payroll, choose <strong>Monthly</strong> and month
        <code>2026-10</code> before import or refresh (the list only shows entries
        for the selected period). Monthly payroll uses <code>YYYY-MM</code> in CSV
        <code>Period</code> or the month picker. Weekly, fortnightly, and daily runs
        need the exact start/end dates. CSV <code>PayrollNumber</code> must match
        each employee’s <strong>Employee ID</strong> on their HR profile (not the
        internal database id).
      </div>

      <div class="card">
        <div class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
          <div>
            <h5>Employee Overtime Entries</h5>
            <div class="text-muted fs-13">
              Match the payroll frequency filter to the run you are preparing.
            </div>
          </div>
          <div class="d-flex align-items-center flex-wrap row-gap-3 gap-2">
            <select v-model="filters.frequency" class="form-select" style="min-width: 160px">
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
              <option value="fortnightly">Fortnightly</option>
              <option value="daily">Daily</option>
            </select>

            <input
              v-if="filters.frequency === 'monthly'"
              v-model="filters.period"
              type="month"
              class="form-control"
              style="min-width: 180px"
            />
            <template v-else>
              <input
                v-model="filters.period_start"
                type="date"
                class="form-control"
                style="min-width: 170px"
              />
              <input
                v-model="filters.period_end"
                type="date"
                class="form-control"
                style="min-width: 170px"
                :disabled="filters.frequency === 'daily'"
              />
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm"
                @click="applySuggestedPeriod()"
              >
                This period
              </button>
            </template>

            <select v-model="filters.employee_id" class="form-select" style="min-width: 220px">
              <option value="">All Employees</option>
              <option
                v-for="employee in employeeOptions"
                :key="employee.id"
                :value="String(employee.id)"
              >
                {{ employeeLabel(employee) }}
              </option>
            </select>

            <button class="btn btn-white border" @click="fetchEntries">Refresh</button>
          </div>
        </div>

        <div
          v-if="entryStore.summary && entryStore.summary.entry_count > 0"
          class="border-bottom bg-light px-4 py-3"
        >
          <div class="row g-3 text-center text-md-start">
            <div class="col-6 col-md-3">
              <div class="text-muted fs-12">Entries</div>
              <div class="fw-semibold fs-16">{{ entryStore.summary.entry_count }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div class="text-muted fs-12">Total hours</div>
              <div class="fw-semibold fs-16">{{ numberValue(entryStore.summary.total_hours) }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div class="text-muted fs-12">Flat amounts (stored)</div>
              <div class="fw-semibold fs-16">{{ moneyValue(entryStore.summary.total_flat_amount) }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div class="text-muted fs-12">
                Est. OT pay (KES)
                <span
                  class="ms-1"
                  title="Basic ÷ monthly hours × hours × rate; uses Payroll Settings divisor and each employee's salary on file."
                >
                  <i class="ti ti-info-circle"></i>
                </span>
              </div>
              <div class="fw-semibold fs-16 text-primary">
                {{ moneyValue(entryStore.summary.estimated_pay_total) }}
              </div>
              <div class="text-muted fs-11">
                Divisor: {{ entryStore.summary.monthly_hours_divisor }} hrs/month
              </div>
            </div>
          </div>
        </div>

        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
            <a-table
              class="table datatable thead-light"
              :columns="columns"
              :data-source="entryStore.entries"
              :loading="entryStore.loading"
              row-key="id"
            >
              <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'employee'">
                  <div class="fw-medium text-gray-9">{{ employeeName(record.employee) }}</div>
                  <div class="text-muted fs-12">{{ record.employee?.employee_id || "-" }}</div>
                </template>
                <template v-else-if="column.key === 'overtime'">
                  <div class="fw-medium text-gray-9">{{ record.overtime?.name || "-" }}</div>
                  <div class="text-muted fs-12">
                    {{ record.overtime?.rate_type || "-" }} {{ record.overtime?.rate || "-" }}x
                  </div>
                </template>
                <template v-else-if="column.key === 'frequency'">
                  <span class="badge bg-outline-secondary">{{ frequencyLabel(record.period_code) }}</span>
                </template>
                <template v-else-if="column.key === 'period'">
                  {{ record.period_label || record.period || "-" }}
                </template>
                <template v-else-if="column.key === 'hours'">
                  {{ numberValue(record.hours) }}
                </template>
                <template v-else-if="column.key === 'custom_rate_multiplier'">
                  {{ numberValue(record.custom_rate_multiplier) }}
                </template>
                <template v-else-if="column.key === 'amount'">
                  {{ moneyValue(record.amount) }}
                </template>
                <template v-else-if="column.key === 'action'">
                  <div class="action-icon d-inline-flex">
                    <a
                      href="javascript:void(0);"
                      class="me-2"
                      data-bs-toggle="modal"
                      data-bs-target="#payroll_overtime_entry_modal"
                      @click="onEdit(record)"
                    >
                      <i class="ti ti-edit"></i>
                    </a>
                    <a href="javascript:void(0);" @click="onDelete(record)">
                      <i class="ti ti-trash"></i>
                    </a>
                  </div>
                </template>
              </template>
            </a-table>
          </div>
        </div>
      </div>
    </div>

    <div class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>

  <payroll-overtime-entry-modal ref="entryModal" @refresh="fetchEntries" />

  <CsvImportModal
    modal-id="overtime_entries_csv_import_modal"
    title="Import Overtime Entries from CSV"
    :description="importDescription"
    template-hint="Columns: PayrollNumber, OvertimeType, Hours, CustomMultiplier (optional), FlatAmount (optional), Period (optional YYYY-MM)."
    template-filename="overtime-entries-import-template.csv"
    :columns="importColumns"
    default-mode="create_or_update"
    :download-template-fn="downloadOvertimeImportTemplate"
    :preview-fn="previewOvertimeImport"
    :apply-fn="applyOvertimeImport"
    @imported="fetchEntries"
  />
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import { usePayrollOvertimeEntryStore } from "@/stores/payrollOvertimeEntryStore";
import PayrollOvertimeEntryModal from "@/components/modal/payroll-overtime-entry-modal.vue";
import CsvImportModal from "@/components/modal/csv-import-modal.vue";
import payrollOvertimeEntryService from "@/services/payrollOvertimeEntry.service.js";
import {
  endDateForFrequency,
  suggestPeriodWindow,
} from "@/utils/payrollPeriodHelpers";

const title = ref("Payroll Components");
const text = ref("Payroll Company Settings");
const text1 = ref("Overtime Entries");
const entryModal = ref(null);
const entryStore = usePayrollOvertimeEntryStore();

const now = new Date();
const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
const filters = reactive({
  frequency: "monthly",
  period: currentMonth,
  period_start: "",
  period_end: "",
  employee_id: "",
});

const importColumns = [
  { key: "line", title: "Line" },
  { key: "payroll_number", title: "Payroll #" },
  { key: "employee_name", title: "Employee" },
  { key: "overtime_type", title: "Overtime type" },
  { key: "hours", title: "Hours" },
  { key: "period_label", title: "Period" },
  { key: "estimated_amount", title: "Est. amount (KES)" },
];

const importPeriodContext = computed(() => ({
  frequency: filters.frequency,
  period: filters.frequency === "monthly" ? filters.period : undefined,
  period_start: filters.frequency === "monthly" ? undefined : filters.period_start,
  period_end: filters.frequency === "monthly" ? undefined : filters.period_end,
}));

const importDescription = computed(() => {
  if (filters.frequency === "monthly") {
    return `Upload a CSV for payroll month ${filters.period || "(select month above)"}. Rows can override Period per line. Preview shows estimated OT using Payroll Settings → monthly hours divisor and each employee's basic salary.`;
  }
  return `Upload a CSV for ${filters.period_start || "…"} to ${filters.period_end || "…"}. Match the period filters above to the payroll run you will generate.`;
});

function downloadOvertimeImportTemplate() {
  return payrollOvertimeEntryService.downloadImportTemplate();
}

function previewOvertimeImport(file, mode) {
  return payrollOvertimeEntryService.previewImport(file, mode, importPeriodContext.value);
}

function applyOvertimeImport(file, mode) {
  return payrollOvertimeEntryService.applyImport(file, mode, importPeriodContext.value);
}

const columns = [
  { title: "Employee", key: "employee" },
  { title: "Overtime", key: "overtime" },
  { title: "Frequency", key: "frequency" },
  { title: "Period", key: "period" },
  { title: "Hours", key: "hours" },
  { title: "Custom Multiplier", key: "custom_rate_multiplier" },
  { title: "Flat Amount", key: "amount" },
  { title: "", key: "action" },
];

const employeeOptions = computed(() =>
  (entryStore.employees || []).slice().sort((a, b) => employeeLabel(a).localeCompare(employeeLabel(b))),
);

function employeeLabel(employee) {
  return `${employeeName(employee)} (${employee?.employee_id || "-"})`;
}

function employeeName(employee) {
  const parts = [employee?.first_name, employee?.middle_name, employee?.last_name].filter(Boolean);
  return parts.join(" ").trim() || "Employee";
}

function frequencyLabel(periodCode) {
  const code = String(periodCode || "");
  if (code.startsWith("weekly:")) return "Weekly";
  if (code.startsWith("fortnightly:")) return "Fortnightly";
  if (code.startsWith("daily:")) return "Daily";
  return "Monthly";
}

function numberValue(value) {
  if (value === null || value === undefined || value === "") return "-";
  return Number(value).toFixed(2);
}

function moneyValue(value) {
  if (value === null || value === undefined || value === "") return "-";
  return `KES ${Number(value).toFixed(2)}`;
}

function requestFilters() {
  return {
    frequency: filters.frequency,
    period: filters.frequency === "monthly" ? filters.period : undefined,
    period_start: filters.frequency === "monthly" ? undefined : filters.period_start,
    period_end: filters.frequency === "monthly" ? undefined : filters.period_end,
    employee_id: filters.employee_id || undefined,
  };
}

async function fetchEntries() {
  await entryStore.fetchAll(requestFilters());
}

function onAddNew() {
  entryModal.value?.resetForm();
}

function onEdit(record) {
  entryModal.value?.openForEdit(record);
}

function onDelete(record) {
  entryModal.value?.openForDelete(record);
}

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function applySuggestedPeriod(anchor = new Date()) {
  const suggestion = suggestPeriodWindow(filters.frequency, anchor);
  filters.period_start = suggestion.periodStart;
  filters.period_end = suggestion.periodEnd;
}

watch(
  () => filters.frequency,
  (frequency) => {
    if (frequency === "monthly") {
      filters.period = filters.period || currentMonth;
      filters.period_start = "";
      filters.period_end = "";
      return;
    }

    filters.period = "";
    applySuggestedPeriod();
  },
);

watch(
  () => filters.period_start,
  (start) => {
    if (filters.frequency === "monthly" || !start) return;
    filters.period_end = endDateForFrequency(filters.frequency, start);
  },
);

onMounted(async () => {
  await entryStore.fetchDependencies();
  await fetchEntries();
});
</script>


